import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { extractIdFromLocationHeader } from '@app/utils/location-header.util';
import { JsonPatchOperation } from '@core/data/models/json-patch.model';
import {
  ApiResponse,
  CreateUiLayoutFieldRequest,
  CreateUiLayoutGroupRequest,
  CreateUiLayoutViewRequest,
  ExportableUiLayoutView,
  UiLayoutFieldDto,
  UiLayoutGroupDto,
  UiLayoutViewDto,
  UpdateUiLayoutFieldRequest,
  UpdateUiLayoutGroupRequest,
  UpdateUiLayoutViewRequest,
} from '../models/view-configurator-dto.model';

@Injectable({
  providedIn: 'root',
})
export class ViewConfiguratorService {
  private readonly baseUrl = `${environment.apiUrl}/ui-layouts`;

  constructor(private http: HttpClient) {}

  private convertToJsonPatch<T extends Record<string, any>>(
    partial: T
  ): JsonPatchOperation[] {
    return Object.entries(partial)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => ({
        op: 'replace' as const,
        path: `/${key}`,
        value: value === null ? null : value,
      }));
  }

  loadViews(dataTypeId?: number): Observable<UiLayoutViewDto[]> {
    const options = dataTypeId
      ? { params: { datatypeid_eq: dataTypeId.toString() } }
      : {};
    return this.http
      .get<
        ApiResponse<UiLayoutViewDto[]>
      >(`${this.baseUrl}/ui-layout-views`, options)
      .pipe(map((response) => response.result));
  }

  getView(viewId: number): Observable<UiLayoutViewDto> {
    return this.http.get<UiLayoutViewDto>(
      `${this.baseUrl}/ui-layout-views/${viewId}`
    );
  }

  createView(request: CreateUiLayoutViewRequest): Observable<UiLayoutViewDto> {
    return this.http
      .post<void>(`${this.baseUrl}/ui-layout-views`, request, {
        observe: 'response',
      })
      .pipe(
        map((response) => ({
          ...request,
          id: extractIdFromLocationHeader(response),
          // Server-assigned fields - will be populated on next load
          dataTypeName: '',
          dataViewId: 0,
          dataViewName: '',
          order: request.order ?? 0,
          isDefault: false,
        }))
      );
  }

  updateView(
    viewId: number,
    request: UpdateUiLayoutViewRequest
  ): Observable<void> {
    const operations = this.convertToJsonPatch(request);
    return this.http.patch<void>(
      `${this.baseUrl}/ui-layout-views/${viewId}`,
      operations,
      {
        headers: { 'Content-Type': 'application/json-patch+json' },
      }
    );
  }

  deleteView(viewId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/ui-layout-views/${viewId}`);
  }

  getGroupsByView(viewId: number): Observable<UiLayoutGroupDto[]> {
    return this.http
      .get<
        ApiResponse<UiLayoutGroupDto[]>
      >(`${this.baseUrl}/ui-layout-views/${viewId}/ui-layout-groups`)
      .pipe(map((response) => response.result));
  }

  getGroupsWithFields(viewId: number): Observable<UiLayoutGroupDto[]> {
    return this.http
      .get<
        ApiResponse<UiLayoutGroupDto[]>
      >(`${this.baseUrl}/ui-layout-views/${viewId}/ui-layout-groups`)
      .pipe(map((response) => response.result));
  }

  createGroup(
    viewId: number,
    request: CreateUiLayoutGroupRequest
  ): Observable<UiLayoutGroupDto> {
    return this.http
      .post<void>(
        `${this.baseUrl}/ui-layout-views/${viewId}/ui-layout-groups`,
        request,
        { observe: 'response' }
      )
      .pipe(
        map((response) => ({
          ...request,
          id: extractIdFromLocationHeader(response),
        }))
      );
  }

  updateGroup(
    viewId: number,
    groupId: number,
    request: UpdateUiLayoutGroupRequest
  ): Observable<void> {
    const operations = this.convertToJsonPatch(request);
    return this.http.patch<void>(
      `${this.baseUrl}/ui-layout-views/${viewId}/ui-layout-groups/${groupId}`,
      operations,
      {
        headers: { 'Content-Type': 'application/json-patch+json' },
      }
    );
  }

  deleteGroup(viewId: number, groupId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/ui-layout-views/${viewId}/ui-layout-groups/${groupId}`
    );
  }

  getFieldsByGroup(
    viewId: number,
    groupId: number
  ): Observable<UiLayoutFieldDto[]> {
    return this.http
      .get<
        ApiResponse<UiLayoutFieldDto[]>
      >(`${this.baseUrl}/ui-layout-views/${viewId}/ui-layout-groups/${groupId}/ui-layout-fields`)
      .pipe(map((response) => response.result));
  }

  createField(
    viewId: number,
    groupId: number,
    request: CreateUiLayoutFieldRequest
  ): Observable<UiLayoutFieldDto> {
    return this.http
      .post<void>(
        `${this.baseUrl}/ui-layout-views/${viewId}/ui-layout-groups/${groupId}/ui-layout-fields`,
        request,
        { observe: 'response' }
      )
      .pipe(
        map((response) => ({
          id: extractIdFromLocationHeader(response),
          uiLayoutGroupId: request.uiGroupId,
          uiLayoutGroup: null,
          label: request.label,
          path: request.path,
          row: request.row,
          column: request.col,
          columnSpan: null,
          placeholder: null,
          isReadOnly: false,
          isNew: true,
          isVisible: request.isVisible ?? true,
          dataPropertyId: 0,
          dataProperty: null,
          dataViewDataPropertyId: 0,
          dataViewDataProperty: null,
        }))
      );
  }

  updateField(
    viewId: number,
    groupId: number,
    fieldId: number,
    request: UpdateUiLayoutFieldRequest
  ): Observable<void> {
    const operations = this.convertToJsonPatch(request);
    return this.http.patch<void>(
      `${this.baseUrl}/ui-layout-views/${viewId}/ui-layout-groups/${groupId}/ui-layout-fields/${fieldId}`,
      operations,
      {
        headers: { 'Content-Type': 'application/json-patch+json' },
      }
    );
  }

  deleteField(
    viewId: number,
    groupId: number,
    fieldId: number
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/ui-layout-views/${viewId}/ui-layout-groups/${groupId}/ui-layout-fields/${fieldId}`
    );
  }

  exportView(
    view: UiLayoutViewDto,
    viewId: number
  ): Observable<ExportableUiLayoutView> {
    return this.getGroupsWithFields(viewId).pipe(
      map((groups) => ({
        title: view.title,
        isActive: view.isActive,
        dataTypeId: view.dataTypeId,
        uiLayoutGroups: groups
          .sort((a, b) => a.order - b.order)
          .map((group) => ({
            title: group.title || '',
            order: group.order,
            uiLayoutFields: (group.uiLayoutFields || []).map((field) => ({
              row: field.row,
              column: field.column,
              label: field.label ?? null,
              placeholder: field.placeholder,
              isReadOnly: field.isReadOnly,
              path: field.path,
              isVisible: field.isVisible,
            })),
          })),
      }))
    );
  }

  importView(
    data: ExportableUiLayoutView,
    dataTypeId: number
  ): Observable<number> {
    const importRequest = {
      title: `${data.title} (importiert)`,
      isActive: false,
      dataTypeId,
      uiLayoutGroups: data.uiLayoutGroups.map((group) => ({
        title: group.title,
        order: group.order,
        uiLayoutFields: group.uiLayoutFields.map((field) => ({
          row: field.row,
          column: field.column,
          label: field.label,
          placeholder: field.placeholder,
          isReadOnly: field.isReadOnly,
          path: field.path,
          isVisible: field.isVisible,
        })),
      })),
    };

    return this.http
      .post<void>(`${this.baseUrl}/ui-layout-views`, importRequest, {
        observe: 'response',
      })
      .pipe(map((response) => extractIdFromLocationHeader(response)));
  }

  restoreDefaultUiLayout(): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/ui-layout-views/restore-default`,
      null
    );
  }
}
